import {
  completeFromList,
  Completion,
  CompletionContext,
  CompletionResult,
  CompletionSource,
  ifNotIn,
} from '@codemirror/autocomplete';
import {
  localCompletionSource,
  scopeCompletionSource,
  snippets,
} from '@codemirror/lang-javascript';

import type {
  Suggestion,
  AutocompletionResult,
  AutocompleteProvider,
} from '../../base-code-editor/auto-completion/autocompletion.extension.js';

/** Javascript keywords for auto-completion. */
const keywords =
  'break case const continue default delete export extends false finally in instanceof let new return static super switch this throw true typeof var yield'
    .split(' ')
    .map((kw) => ({
      label: kw,
      type: 'keyword',
    }));

/** Sources that shouldn't be added to the autocomplete list. */
const dontComplete = [
  'TemplateString',
  'String',
  'RegExp',
  'LineComment',
  'BlockComment',
  'VariableDefinition',
  'TypeDefinition',
  'Label',
  'PropertyDefinition',
  'PropertyName',
  'PrivatePropertyDefinition',
  'PrivatePropertyName',
];

/**
 * Auto-complete provider for javascript.
 * @param context - context for completion
 */
export const javascriptAutocompleteProvider: AutocompleteProvider = async (
  context: CompletionContext,
): Promise<AutocompletionResult> => {
  // Locally defined variables
  const localCompletionResult = localCompletionSource(context);
  // Javascript snippets and keywords
  const javascriptCompletionResult = await javascriptCompletionSource()(
    context,
  );
  // Scope methods + variables
  const scopeCompletionResult = await scopeCompletionSource(globalThis)(
    context,
  );

  // match alphanumeric word before the cursor
  const before = context.matchBefore(/\w+/);

  const completions = extractCompletions([
    localCompletionResult,
    javascriptCompletionResult,
    scopeCompletionResult,
  ]);

  if (completions.length === 0) {
    return { from: context.pos, suggestions: [] };
  }

  return {
    from: before ? before.from : context.pos,
    keepOrder: true,
    validFor: /\w+/,
    suggestions: transformSuggestions(completions),
  };
};

/**
 * Completion source for javascript snippets and keywords.
 */
const javascriptCompletionSource = (): CompletionSource => {
  return ifNotIn(dontComplete, completeFromList(snippets.concat(keywords)));
};

/**
 * Extract completions from completion results.
 * @param completionResultList - completion result list containing several completion results
 */
const extractCompletions: (
  completionResultList: (CompletionResult | null)[],
) => Completion[] = (completionResultList) => {
  let completions: Completion[] = [];

  for (const completionResult of completionResultList) {
    if (completionResult !== null) {
      completions = completions.concat(completionResult.options);
    }
  }
  return completions;
};

/**
 * Transform completions from code-mirror object notation to suggestions.
 * @param completions - completions array
 */
const transformSuggestions: (completions: Completion[]) => Suggestion[] = (
  completions,
) => {
  const suggestions: Suggestion[] = [];

  for (const completion of completions) {
    suggestions.push({
      postfix: completion.detail,
      label: completion.label,
      type: completion.type,
      apply: completion.apply,
    });
  }

  return suggestions;
};
